import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Card,
  DataState,
  PageHeader,
  SearchInput,
  Select,
  Table,
  type Column,
} from "@vms/ui-web";
import { useOrgContext } from "../context/OrgContext";
import { useQuery } from "../api/queries";

interface DriverRow {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string | null;
  phoneNumber?: string | null;
  companyName?: string | null;
  venueId?: string | null;
  anonymisedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export function Drivers() {
  const { currentOrg, venues } = useOrgContext();
  const [search, setSearch] = useState("");
  const [anonymisedFilter, setAnonymisedFilter] = useState<string>("");
  const [lockedFilter, setLockedFilter] = useState<string>("");
  const [venueFilter, setVenueFilter] = useState<string>("");

  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (anonymisedFilter) params.set("anonymised", anonymisedFilter);
  if (lockedFilter) params.set("locked", lockedFilter);
  if (venueFilter) params.set("venueVisited", venueFilter);

  const endpoint = currentOrg
    ? `/api/organisations/${currentOrg.id}/drivers${params.toString() ? `?${params.toString()}` : ""}`
    : null;
  const driversQuery = useQuery<DriverRow[]>(endpoint);

  if (!currentOrg) return null;

  const venueLookup = new Map(venues.map((v) => [v.id, v.name]));

  const columns: Column<DriverRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (d) => (
        <Link
          to={`/org/${currentOrg.slug}/drivers/${d.id}`}
          className="font-medium text-gray-900 hover:underline"
        >
          {d.displayName}
        </Link>
      ),
    },
    {
      key: "email",
      header: "Email",
      render: (d) => <span className="text-sm text-gray-700">{d.email || "—"}</span>,
    },
    {
      key: "phone",
      header: "Phone",
      render: (d) => <span className="text-sm text-gray-500">{d.phoneNumber || "—"}</span>,
    },
    {
      key: "venueLock",
      header: "Venue lock",
      render: (d) =>
        d.venueId ? (
          <Badge className="bg-blue-100 text-blue-700">{venueLookup.get(d.venueId) ?? "locked"}</Badge>
        ) : (
          <span className="text-xs text-gray-400">org-shared</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (d) =>
        d.anonymisedAt ? (
          <Badge className="bg-gray-100 text-gray-700">Anonymised</Badge>
        ) : (
          <Badge className="bg-green-100 text-green-700">Active</Badge>
        ),
    },
  ];

  const rows = driversQuery.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader title="Drivers" subtitle={`Drivers across ${currentOrg.name}.`} />

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[16rem]">
            <SearchInput onSearch={setSearch} placeholder="Search name or email" />
          </div>
          <Select
            label="Anonymised"
            value={anonymisedFilter}
            onChange={(e) => setAnonymisedFilter(e.target.value)}
            options={[
              { value: "", label: "Any" },
              { value: "no", label: "Active only" },
              { value: "yes", label: "Anonymised only" },
            ]}
          />
          <Select
            label="Lock"
            value={lockedFilter}
            onChange={(e) => setLockedFilter(e.target.value)}
            options={[
              { value: "", label: "Any" },
              { value: "no", label: "Org-shared" },
              { value: "yes", label: "Venue-locked" },
            ]}
          />
          <Select
            label="Visited venue"
            value={venueFilter}
            onChange={(e) => setVenueFilter(e.target.value)}
            options={[
              { value: "", label: "Any venue" },
              ...venues.map((v) => ({ value: v.id, label: v.name })),
            ]}
          />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <DataState
          loading={driversQuery.loading}
          error={driversQuery.error}
          empty={!driversQuery.loading && rows.length === 0}
          emptyMessage="No drivers match your filters yet."
        >
          <Table<DriverRow> columns={columns} data={rows} keyExtractor={(d) => d.id} />
        </DataState>
      </Card>
    </div>
  );
}
